import { useMemo } from 'react';
import type { ChartCardTableColumnDef } from '../components/table/tanstack';
import type { ChartCardChartCardsDefinition, ChartCardTableCardDefinition, ChartChoicesMap, ElementsDescriptionsMap, FinaliseDimensionValuesResult, FinaliseMetricsResult, ValidatedChartCardProps, ValidatedDataDefinitionProps } from '../internalTypes';
import type { ChartCardCardsProps, ChartCardChartDefinition, ChartCardChartProps, ChartCardClientDataProps, ChartDataRow, ColumnsProps } from '../types';
import { createOtherChoicesMap } from '../utils/choices';
import { finaliseChoicesChart, finaliseChoicesTable, finaliseElementsDescriptionsMap, finaliseFirstAxis, finaliseSecondAxis, finaliseThirdAxis } from '../utils/finalise';
import { errorLocal } from '../utils/generics';
import { useChartCardConstants } from './useChartCardConstants';
const getChartAndColumnPropsFromCard = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(card: ChartCardCardsProps<DataKey, DataRow>): {
  cardType: 'table';
  columnsProp: ColumnsProps<DataKey, DataRow> | undefined;
  chartProp: ChartCardChartProps<DataKey, DataRow> | undefined;
} | {
  cardType: 'chart' | 'performance';
  chartProp: ChartCardChartProps<DataKey, DataRow>;
  columnsProp: ColumnsProps<DataKey, DataRow> | undefined;
} => {
  const {
    type: cardType,
    columns: columnsProp,
    chart,
    performance
  } = card;
  if (cardType === 'table') {
    return {
      cardType,
      columnsProp,
      chartProp: chart ?? performance
    };
  }
  return {
    cardType,
    chartProp: chart ?? performance as ChartCardChartProps<DataKey, DataRow>,
    columnsProp
  };
};
export const useValidateChartCardProps = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  dataDefinition: {
    result: commonPropsResult,
    dimensionsMap,
    metricsVariantMap,
    metricsMap,
    dimensionsValuesMap
  },
  card,
  data: {
    isLoading,
    data
  }
}: {
  dataDefinition: ValidatedDataDefinitionProps<DataKey, DataRow>;
  card: ChartCardCardsProps<DataKey, DataRow>;
  data: ChartCardClientDataProps<DataKey, DataRow>;
}): ValidatedChartCardProps<DataKey, DataRow> => {
  const {
    chartSeriesColors
  } = useChartCardConstants();
  const dataLoading = isLoading || data == null || commonPropsResult === 'loading';
  const {
    cardType,
    columnsProp,
    chartProp
  } = getChartAndColumnPropsFromCard(card);
  const chartPossible = chartProp != null;
  const validChartType = useMemo(() => {
    if (dataLoading || commonPropsResult !== 'valid' || chartProp == null) {
      return false;
    }
    if (chartProp.type === 'scatter') {
      return chartProp.otherType == null;
    }
    return chartProp.otherType !== chartProp.type;
  }, [dataLoading, chartProp, commonPropsResult]);
  const firstAxis = useMemo<FinaliseMetricsResult<DataKey, DataRow> | FinaliseDimensionValuesResult<DataKey, DataRow>>(() => {
    if (!chartPossible) {
      return {
        type: 'undefined'
      };
    }
    if (validChartType) {
      return finaliseFirstAxis<DataKey, DataRow>(chartProp.type, chartProp.firstAxis, dimensionsMap, metricsMap, metricsVariantMap, dimensionsValuesMap, data, chartSeriesColors);
    }
    return {
      type: 'invalid'
    };
  }, [chartPossible, validChartType, chartProp?.type, chartProp?.firstAxis, dimensionsMap, metricsMap, metricsVariantMap, dimensionsValuesMap, data, chartSeriesColors]);
  const secondAxis = useMemo<FinaliseMetricsResult<DataKey, DataRow>>(() => {
    if (!chartPossible) {
      return {
        type: 'undefined'
      };
    }
    if (validChartType) {
      return finaliseSecondAxis<DataKey, DataRow>({
        axisChoice: 'second',
        chartType: chartProp.type,
        axis: chartProp.secondAxis,
        dimensionsMap,
        metricsMap,
        metricsVariantMap,
        dimensionsValuesMap,
        otherChoicesMap: createOtherChoicesMap(firstAxis.choices),
        data,
        chartAvailableColors: chartSeriesColors
      });
    }
    return {
      type: 'invalid'
    };
  }, [chartPossible, validChartType, chartProp?.type, chartProp?.secondAxis, dimensionsMap, metricsMap, metricsVariantMap, dimensionsValuesMap, firstAxis.choices, data, chartSeriesColors]);
  const otherSecondAxis = useMemo<FinaliseMetricsResult<DataKey, DataRow>>(() => {
    if (!chartPossible) {
      return {
        type: 'undefined'
      };
    }
    if (validChartType) {
      return finaliseSecondAxis<DataKey, DataRow>({
        axisChoice: 'otherSecond',
        chartType: chartProp.otherType ?? chartProp.type,
        axis: chartProp.otherSecondAxis,
        dimensionsMap,
        metricsMap,
        metricsVariantMap,
        dimensionsValuesMap,
        otherChoicesMap: createOtherChoicesMap(firstAxis.choices, secondAxis.choices),
        data,
        chartAvailableColors: chartSeriesColors
      });
    }
    return {
      type: 'invalid'
    };
  }, [chartPossible, validChartType, chartProp?.otherType, chartProp?.type, chartProp?.otherSecondAxis, dimensionsMap, metricsMap, metricsVariantMap, dimensionsValuesMap, firstAxis.choices, secondAxis.choices, data, chartSeriesColors]);
  const thirdAxis = useMemo<FinaliseDimensionValuesResult<DataKey, DataRow>>(() => {
    if (!chartPossible) {
      return {
        type: 'undefined'
      };
    }
    if (validChartType) {
      return finaliseThirdAxis<DataKey, DataRow>({
        chartType: chartProp.type,
        axis: chartProp.thirdAxis,
        dimensionsMap,
        metricsMap,
        metricsVariantMap,
        dimensionsValuesMap,
        otherChoicesMap: createOtherChoicesMap(firstAxis.choices, secondAxis.choices, otherSecondAxis.choices),
        data,
        chartAvailableColors: chartSeriesColors
      });
    }
    return {
      type: 'invalid'
    };
  }, [chartPossible, validChartType, chartProp?.type, chartProp?.thirdAxis, dimensionsMap, metricsMap, metricsVariantMap, dimensionsValuesMap, firstAxis.choices, secondAxis.choices, otherSecondAxis.choices, data, chartSeriesColors]);
  const {
    chart: chart_0,
    columns: columns_0,
    choicesList: choicesList_0,
    choicesMap: choicesMap_0,
    elementsDescriptionsMap: elementsDescriptionsMap_0
  } = useMemo<{
    chart?: ChartCardChartDefinition<DataKey, DataRow>;
    columns?: Array<ChartCardTableColumnDef<DataKey, DataRow>>;
    choicesList?: DataKey[];
    choicesMap?: ChartChoicesMap<DataKey, DataRow>;
    elementsDescriptionsMap?: ElementsDescriptionsMap<DataKey>;
  }>(() => {
    if (dataLoading) {
      return {};
    }
    if (!validChartType && cardType !== 'table' || dimensionsMap == null || metricsMap == null || metricsVariantMap == null || dimensionsValuesMap == null || data == null) {
      errorLocal(`useValidateChartCardProps chart invalid requirements`, {
        validChartType,
        dimensionsMap,
        metricsMap,
        metricsVariantMap,
        dimensionsValuesMap,
        data
      });
      return {};
    }
    if (firstAxis.type === 'invalid' || secondAxis.type === 'invalid' || otherSecondAxis.type === 'invalid' || thirdAxis.type === 'invalid') {
      errorLocal(`useValidateChartCardProps chart invalid`, {
        firstAxis,
        secondAxis,
        otherSecondAxis,
        thirdAxis
      });
      return {};
    }
    const choicesList: DataKey[] = [];
    const partialChoicesMap: Partial<ChartChoicesMap<DataKey, DataRow>> = {};
    let chart: ChartCardChartDefinition<DataKey, DataRow> | undefined;
    let columns: Array<ChartCardTableColumnDef<DataKey, DataRow>> | undefined;
    if (cardType === 'table') {
      columns = finaliseChoicesTable<DataKey, DataRow>({
        columnsProp,
        dimensionsMap,
        metricsMap,
        metricsVariantMap,
        dimensionsValuesMap,
        choicesList,
        partialChoicesMap,
        data,
        chartAvailableColors: chartSeriesColors
      });
    } else {
      chart = finaliseChoicesChart<DataKey, DataRow>({
        chartProp,
        firstAxis,
        secondAxis,
        otherSecondAxis,
        thirdAxis,
        choicesList,
        partialChoicesMap
      });
      if (chart == null) {
        return {};
      }
    }
    const choicesMap = partialChoicesMap as ChartChoicesMap<DataKey, DataRow>;
    const elementsDescriptionsMap = finaliseElementsDescriptionsMap<DataKey, DataRow>({
      metricsMap,
      dimensionsMap,
      dimensionsValuesMap,
      choicesMap
    });
    return {
      choicesList,
      choicesMap,
      chart,
      columns,
      elementsDescriptionsMap
    };
  }, [dataLoading, validChartType, cardType, dimensionsMap, metricsMap, metricsVariantMap, dimensionsValuesMap, data, firstAxis, secondAxis, otherSecondAxis, thirdAxis, chartProp, columnsProp, chartSeriesColors]);
  if (commonPropsResult === 'invalid') {
    return {
      result: cardType === 'chart' || cardType === 'performance' ? 'invalidChart' : 'invalidTable'
    };
  }
  if (dataLoading) {
    return {
      result: cardType === 'chart' || cardType === 'performance' ? 'loadingChart' : 'loadingTable'
    };
  }
  if ((cardType === 'chart' || cardType === 'performance') && commonPropsResult === 'valid' && validChartType && dimensionsMap != null && metricsVariantMap != null && metricsMap != null && dimensionsValuesMap != null && chart_0 != null && choicesList_0 != null && choicesMap_0 != null && elementsDescriptionsMap_0 != null) {
    const card_0 = (cardType === 'performance' ? {
      type: 'performance',
      performance: chart_0
    } : {
      type: 'chart',
      chart: chart_0
    }) as ChartCardChartCardsDefinition<DataKey, DataRow>;
    return {
      result: 'validChart',
      dimensionsMap,
      metricsVariantMap,
      metricsMap,
      dimensionsValuesMap,
      card: card_0,
      choicesList: choicesList_0,
      choicesMap: choicesMap_0,
      elementsDescriptionsMap: elementsDescriptionsMap_0
    };
  }
  if (cardType === 'table' && commonPropsResult === 'valid' && dimensionsMap != null && metricsVariantMap != null && metricsMap != null && dimensionsValuesMap != null && columns_0 != null && choicesList_0 != null && choicesMap_0 != null && elementsDescriptionsMap_0 != null) {
    const card_1: ChartCardTableCardDefinition<DataKey, DataRow> = {
      type: 'table',
      columns: columns_0
    };
    return {
      result: 'validTable',
      dimensionsMap,
      metricsVariantMap,
      metricsMap,
      dimensionsValuesMap,
      card: card_1,
      choicesList: choicesList_0,
      choicesMap: choicesMap_0,
      elementsDescriptionsMap: elementsDescriptionsMap_0
    };
  }
  errorLocal('useValidateChartCardProps invalid', {
    validChartType,
    dimensionsMap,
    metricsVariantMap,
    metricsMap,
    dimensionsValuesMap,
    firstAxis,
    secondAxis,
    otherSecondAxis,
    thirdAxis,
    choicesList: choicesList_0,
    choicesMap: choicesMap_0,
    elementsDescriptionsMap: elementsDescriptionsMap_0,
    chart: chart_0,
    axisProp: chartProp
  });
  return {
    result: cardType === 'table' ? 'invalidTable' : 'invalidChart',
    dimensionsMap,
    metricsVariantMap,
    metricsMap
  };
};